import { Layout } from '../../../components/Layout'
import { HeadingPages } from '../../../components/HeadingPages'
import { Button, Center, Flex, Input, Select, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useToast } from '@chakra-ui/react'
import { api } from '../../../services/api'
import { useMutation } from 'react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useEffect } from 'react'
import { useExcelGenerator } from '../../../hooks/useExcelGenerator'

const schema = z.object({
  dataInicio: z.string().optional(),
  dataFim: z.string().optional(),
  pedido_venda: z.string().optional(),
  pedido_compra: z.string().optional(),
  nfe: z.string().optional(),
  select: z.string().optional(),
})

async function searchPedido(data) {
  const response = await api.post(
    `/site/v2/crm-controle/listar-pedidos-compra
  `,
    data
  )
  return response.data
}

export function BuscarPedido() {
  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    setValue,
    setError,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      dataInicio: new Date().toISOString().split('T')[0].split('-').slice(0, 2).join('-') + '-01',
      dataFim: new Date().toISOString().split('T')[0],
    },
  })

  const { generateExcel } = useExcelGenerator()

  const { data, mutateAsync, isLoading, error, isFetching } = useMutation({
    mutationFn: async (data) => searchPedido(data),
  })

  const select = watch('select')
  const dataDe = watch('dataInicio')
  const dataAte = watch('dataFim')

  const toast = useToast({
    title: 'Atenção!',
    description: 'Filtre por Data ou Número (Não pode os 2 juntos)',
    status: 'info',
    duration: 5000,
    isClosable: true,
  })

  async function handleSearchPedido(data) {
    const conditionalVariable = watch('select')

    if (dataDe && dataAte && conditionalVariable) {
      toast()
      return
    }

    if (conditionalVariable === 'P' && !data.pedido_venda) {
      setError('pedido_venda', {
        type: 'manual',
        message: 'Pedido Venda obrigatório',
      })
      return
    }

    if (conditionalVariable === 'N' && !data.pedido_compra) {
      setError('pedido_compra', {
        type: 'manual',
        message: 'Pedido Compra obrigatório',
      })
      return
    }

    if (conditionalVariable === 'NFE' && !data.nfe) {
      setError('nfe', {
        type: 'manual',
        message: 'NF-e obrigatória',
      })
      return
    }

    const params = {
      data_inicio: data.dataInicio,
      data_fim: data.dataFim,
      selecao: conditionalVariable === '' ? 'N' : conditionalVariable,
      pedido_venda: data.pedido_venda,
      pedido_compra: data.pedido_compra,
      nfe: data.nfe,
    }

    try {
      await mutateAsync(params)
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    setValue('pedido_venda', '')
    setValue('pedido_compra', '')
    setValue('nfe', '')
  }, [select, setValue])

  async function handleGenerateExcel() {
    const header = ['Nfe', 'Pedido Venda', 'Pedido Compra']
    const dataItems = data?.map((item) => [item.numero_nota, item.numero_pedido.numpedido, item.numero_pedido.nunota.join(', ')])

    await generateExcel(header, dataItems, 'Pedidos')
  }

  return (
    <>
      <HeadingPages name="Buscar Pedido de Compra/Venda" />
      <Layout>
        <Flex maxW="900px" w="full" alignItems="start" gap={2} mx="auto" as="form" onSubmit={handleSubmit(handleSearchPedido)}>
          <Input type="date" {...register('dataInicio')} />
          <Input type="date" {...register('dataFim')} />
          <Select {...register('select')}>
            <option value="">Selecione</option>
            <option value="P">Pedido Venda</option>
            <option value="N">Pedido Compra</option>
            <option value="NFE">Nfe</option>
          </Select>
          {select === 'P' && (
            <Flex flexDir="column" gap={2} w="100%" justifyContent="flex-end">
              <Input placeholder="Pedido Venda" {...register('pedido_venda')} />
              {errors.pedido_venda && <Text color="red.500">{errors.pedido_venda.message}</Text>}
            </Flex>
          )}
          {select === 'N' && (
            <Flex flexDir="column" gap={2} w="100%">
              <Input placeholder="Pedido Compra" {...register('pedido_compra')} />
              {errors.pedido_compra && <Text color="red.500">{errors.pedido_compra.message}</Text>}
            </Flex>
          )}
          {select === 'NFE' && (
            <Flex flexDir="column" gap={2} w="100%">
              <Input placeholder="Nfe" {...register('nfe')} />
              {errors.nfe && <Text color="red.500">{errors.nfe.message}</Text>}
            </Flex>
          )}
          <Button colorScheme="blue" maxW="200px" w="full" type="submit">
            Buscar
          </Button>
        </Flex>

        <Flex flexDir="column" gap={2} maxW="500px" w="full" mx="auto" mt="5">
          {data?.length === 0 && <Text textAlign="center">Nenhum pedido encontrado</Text>}
          {data?.length > 0 && (
            <Text textAlign="center">
              Foram encontrados <strong>{data?.length}</strong> pedidos
            </Text>
          )}

          {isLoading ? (
            <Center>
              <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
            </Center>
          ) : error ? (
            <p>Ocorreu um erro ao buscar o pedido</p>
          ) : null}

          {isFetching && <p>Atualizando...</p>}
        </Flex>

        {data?.length > 0 && (
          <TableContainer w="100%" maxW="760px" mx="auto">
            <Button colorScheme="blue" maxW="200px" w="full" onClick={handleGenerateExcel} isLoading={isLoading} loadingText="Gerando Excel">
              Gerar Excel
            </Button>
            <Table variant="simple" w="100%">
              <Thead>
                <Tr>
                  <Th textAlign="center">Nfe</Th>
                  <Th textAlign="center">Pedido Venda</Th>
                  <Th textAlign="center">Pedido Compra</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data?.map((item, index) => (
                  <Tr key={index}>
                    <Td textAlign="center">
                      <Text
                        as="a"
                        color="blue.500"
                        _hover={{ color: 'blue.700' }}
                        textDecoration="underline"
                        href={item.link_nfe}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item.numero_nota}
                      </Text>
                    </Td>
                    <Td textAlign="center">
                      <Text
                        color="blue.500"
                        _hover={{ color: 'blue.700' }}
                        textDecoration="underline"
                        as="a"
                        href={`${process.env.REACT_APP_BASE_URL}/site/v2/orcamento/visualiza-pdf-vendas-interno/${item.numero_nota}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item.numero_pedido.numpedido}
                      </Text>
                    </Td>
                    <Td textAlign="center">
                      <Flex flexDir="column" gap={1}>
                        {item.numero_pedido.nunota?.map((item) => (
                          <Text
                            key={item}
                            as="a"
                            href={`${process.env.REACT_APP_BASE_URL}/site/v2/orcamento/visualiza-pedido-compra-v2/${item}`}
                            target="_blank"
                            rel="noreferrer"
                            color="blue.500"
                            _hover={{ color: 'blue.700' }}
                            textDecoration="underline"
                          >
                            {item}
                          </Text>
                        ))}
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Layout>
    </>
  )
}
